import { type AdSource } from '@kijiji/generated/graphql-types'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { HideAtOrLarger, ShowAtOrLarger, ShowWhenSmaller } from '@/components/shared/breakpoint'
import {
  type RelativeTimeProps,
  RelativeTime,
} from '@/components/shared/relative-time/RelativeTime'
import { ListingPosterReview } from '@/components/srp/listing-card/listing-details/ListingPosterReview'
import {
  ListingDetailsContainer,
  ListingLocationLabel,
  ListingProximityLabel,
  SortingDateLabel,
} from '@/components/srp/listing-card/listing-details/styled'
import { ListingBadgeIcons } from '@/components/srp/listing-card/ListingCard'
import { ShippingBadge } from '@/components/srp/listing-card/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { MINIMUM_RADIUS_VALUE_IN_KM } from '@/features/listing/constants/listingDetails'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { BodyText } from '@/ui/atoms/body-text'
import { Flex } from '@/ui/atoms/flex'

export type ListingDetailsProps = {
  /**
   * Listing neighbourhood name
   */
  neighbourhoodName?: string
  /**
   * Sorting date for the listing
   */
  sortingDate?: string | null
  /**
   * Listing location name
   */
  locationName?: string | null
  /**
   * Defines if this listing will be shipped by seller
   */
  isShippedBySeller?: boolean
  /**
   * Ad source
   */
  listingAdSource?: AdSource
  /**
   * Nearest intersection within the search radius
   */
  nearestIntersection?: string
  /**
   * Distance from the search location, in metres.
   */
  proximity?: string
  /**
   * Defines if the date should be formatted to short format. E.g: "< 1 min" or "4 d"
   */
  dateFormat?: 'responsive' | 'short' | 'default'

  /** Review rating of poster */
  rating?: number | null

  /** Whether the poster is verified */
  verified?: boolean | null
}

export const ListingDetails: FC<ListingDetailsProps> = ({
  sortingDate,
  isShippedBySeller,
  listingAdSource,
  locationName,
  proximity,
  dateFormat,
  neighbourhoodName,
  nearestIntersection,
  rating,
  verified,
}) => {
  const { colors, spacing } = useTheme()
  const { t } = useTranslation([TRANSLATION_KEYS.LISTING])
  const { location } = useGetLocation()

  // Should not return the  component if listing doesn't have any details defined
  if (!locationName && !sortingDate) return null

  const getLocationNameToShow = () => {
    // Radius is available in km
    const radius = location.area?.radius ? location.area.radius : undefined
    const isRadiusLessThanForty = radius && radius <= MINIMUM_RADIUS_VALUE_IN_KM

    if (neighbourhoodName) {
      return `${neighbourhoodName}, ${locationName}`
    }

    if (isRadiusLessThanForty && nearestIntersection) {
      return nearestIntersection
    }

    return locationName ?? undefined
  }

  const locationLabel = getLocationNameToShow()

  const hasTopAdLabelEnabled = listingAdSource === 'TOP_AD'

  const getDivider = () => (
    <BodyText
      color={colors.grey.light3}
      as="span"
      role="separator"
      data-testid="dot-divider"
      size="xSmall"
    >
      •
    </BodyText>
  )

  const getFormattedDate = () => {
    const props: RelativeTimeProps = {
      color: colors.grey.light1,
      date: sortingDate ?? '',
      size: 'xSmall',
      style: { whiteSpace: 'nowrap' },
    }

    if (dateFormat !== 'responsive') {
      return <RelativeTime {...props} data-testid="listing-date" variant={dateFormat} />
    }

    return (
      <>
        <HideAtOrLarger breakpoint="medium">
          <RelativeTime {...props} data-testid="listing-date-mobile" variant="short" />
        </HideAtOrLarger>

        <ShowAtOrLarger breakpoint="medium">
          <RelativeTime {...props} data-testid="listing-date" variant="default" />
        </ShowAtOrLarger>
      </>
    )
  }

  return (
    <Flex flexDirection="column" gap={spacing.mini}>
      <ListingDetailsContainer data-testid="listing-details">
        {locationLabel ? (
          <ListingLocationLabel
            color={colors.grey.light1}
            data-testid="listing-location"
            size="xSmall"
          >
            {locationLabel}
          </ListingLocationLabel>
        ) : null}

        {proximity ? (
          <>
            {locationLabel ? getDivider() : null}

            <ListingProximityLabel
              color={colors.grey.light1}
              data-testid="listing-proximity"
              size="xSmall"
            >
              {proximity}
            </ListingProximityLabel>
          </>
        ) : null}

        <SortingDateLabel hasTopProvAdEnabled={hasTopAdLabelEnabled}>
          {sortingDate ? (
            <>
              {locationLabel || proximity ? getDivider() : null}

              {getFormattedDate()}
            </>
          ) : null}
        </SortingDateLabel>

        {isShippedBySeller && (
          <>
            {(locationLabel || sortingDate) && (
              <ShowWhenSmaller breakpoint="medium">{getDivider()}</ShowWhenSmaller>
            )}
            <ShowWhenSmaller breakpoint="medium" data-testid={'shipping-badge-mweb'}>
              <ShippingBadge as="div">
                {ListingBadgeIcons.shippedBySeller}
                <span>{t(`listing:flag.shipping`)}</span>
              </ShippingBadge>
            </ShowWhenSmaller>
          </>
        )}
      </ListingDetailsContainer>

      <ListingPosterReview rating={rating} verified={verified} />
    </Flex>
  )
}
